/**
 * Big-screen layouts (§15.8), one per phase, for a 16:9 TV seen from across the room. They are
 * pure functions of the public view (plus the server time and, at the end, the replay): the
 * big screen receives nothing else, so it can show nothing private.
 *
 * What they never show (§15.8): handshake counts per player (only the room's total), roles or
 * teams before the end, or who has voted during a ballot (only how many).
 */
import { formatDuration, TIMER_KEYS, TIMER_LABELS } from "@handshake/config/catalog";
import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { QrCode } from "../../components/QrCode.tsx";
import { PlayerChip } from "../../components/PlayerChip.tsx";
import {
  BALLOT_OUTCOMES,
  outcomeText,
  PHASE_TITLES,
  presetName,
  roleName,
} from "../../lib/labels.ts";
import { formatClock, remainingMs } from "../../lib/time.ts";
import type {
  BallotReveal,
  Bulletin,
  PublicView,
  ReplayData,
  RosterEntry,
} from "../../lib/types.ts";
import { living, nameOf, playerOf } from "../../lib/view.ts";
import { candlesMs } from "../Dusk.tsx";
import { Replay } from "../End.tsx";

/** How long (server time) the previous Council's ballot reveal stays up at the next Mingle. */
export const REVEAL_HOLD_MS = 20_000;

export interface ScreenProps {
  readonly pub: PublicView;
  /** The server's clock now. */
  readonly now: number;
  readonly joinUrl: string;
  readonly roomCode: string;
  readonly replay: ReplayData | null;
  /** Survey progress at the end, when the room shares game data (server `room` message). */
  readonly surveys?: { readonly submitted: number; readonly players: number } | null;
}

/** The layout for the current phase. */
export function PhaseLayout(props: ScreenProps) {
  const { pub, now } = props;
  const kind = pub.phase.kind;
  if (kind === "lobby") return <LobbyLayout {...props} />;
  if (kind === "arrival") return <ArrivalLayout {...props} />;
  if (kind === "mingle") {
    const round = pub.phase.round;
    const last = pub.revealedBallots.filter((b) => b.round === round - 1);
    const started = pub.phase.startedAt;
    if (last.length > 0 && started !== null && now - started < REVEAL_HOLD_MS) {
      return <RevealLayout pub={pub} reveals={last} />;
    }
    return <MingleLayout {...props} />;
  }
  if (kind === "dusk") return <DuskLayout {...props} />;
  if (kind === "ended") return <EndLayout {...props} />;
  return <CouncilLayout {...props} />;
}

/** The phase header: title, round, the big countdown, the rules badge and request counts. */
export function ScreenHeader({ pub, now }: { pub: PublicView; now: number }) {
  const phase = pub.phase;
  const ms = remainingMs(phase, now);
  const rounds = phase.rounds;
  const sub =
    phase.kind === "lobby"
      ? `${pub.roster.length} guests`
      : phase.kind === "arrival"
        ? "Before Round 1"
        : phase.kind === "ended"
          ? "Game over"
          : `Round ${phase.round}${rounds ? ` of ${rounds}` : ""}`;
  return (
    <header className="screen-head">
      <div className="screen-brand">HANDSHAKE</div>
      <div className="screen-phase">
        <div className="screen-title" data-testid="screen-phase">
          {PHASE_TITLES[phase.kind]}
          {phase.kind === "ballot" && phase.ballot === 2 ? " 2" : ""}
        </div>
        <div className="screen-sub">
          {sub}
          {phase.kind === "mingle" && phase.closing ? " · finishing handshakes" : ""}
        </div>
      </div>
      <div className="screen-meta">
        <ScreenBadge pub={pub} />
        <RequestCountsLine pub={pub} />
      </div>
      {ms !== null && phase.kind !== "mingle" ? (
        <div className={`screen-clock ${ms <= 30_000 ? "low" : ""}`} data-testid="screen-clock">
          {formatClock(ms)}
        </div>
      ) : null}
    </header>
  );
}

export function ScreenBadge({ pub }: { pub: PublicView }) {
  const rules = pub.rules;
  if (!rules) return null;
  return (
    <div className="screen-badge" data-testid="screen-rules-badge">
      {rules.badge}
      {rules.experimental ? <span className="tag">Experimental</span> : null}
      {rules.rigged ? <span className="tag">Rigged game</span> : null}
    </div>
  );
}

function RequestCountsLine({ pub }: { pub: PublicView }) {
  const more = pub.moreTimeRequests;
  const pause = pub.pauseRequests;
  if (more === 0 && pause === 0) return null;
  const parts: string[] = [];
  if (more > 0) parts.push(`${more} ${more === 1 ? "player wants" : "players want"} more time`);
  if (pause > 0)
    parts.push(`${pause} ${pause === 1 ? "player asked" : "players asked"} for a pause`);
  return (
    <div className="screen-requests" data-testid="screen-requests">
      {parts.join(" · ")}
    </div>
  );
}

function BigLeakTrack({ pub }: { pub: PublicView }) {
  if (pub.leakLimit === null) return null;
  const pct = Math.min(100, (pub.leakTrack / pub.leakLimit) * 100);
  const tally =
    pub.finalTallyThreshold !== null ? (pub.finalTallyThreshold / pub.leakLimit) * 100 : null;
  return (
    <div className="screen-leak" data-testid="screen-leak-track">
      <div className="row">
        <span className="grow screen-label">Leak track</span>
        <strong className="screen-leak-value">
          {pub.leakTrack} / {pub.leakLimit}
        </strong>
      </div>
      <div className="screen-meter" aria-hidden="true">
        <div style={{ width: `${pct}%` }} />
        {tally !== null ? <span className="tally-mark" style={{ left: `${tally}%` }} /> : null}
      </div>
      {pub.finalTallyThreshold !== null ? (
        <div className="screen-faint">Final Tally threshold: {pub.finalTallyThreshold}</div>
      ) : null}
    </div>
  );
}

function Roster({ roster }: { roster: readonly RosterEntry[] }) {
  const alive = living(roster);
  const ghosts = roster.filter((p) => !p.alive || p.removed);
  return (
    <div className="screen-roster" data-testid="screen-roster">
      <div className="screen-label">Guests · {alive.length} living</div>
      <div className="screen-chips">
        {alive.map((p) => (
          <PlayerChip key={p.id} player={p} />
        ))}
      </div>
      {ghosts.length > 0 ? (
        <>
          <div className="screen-label" style={{ marginTop: "0.6em" }}>
            Ghosts
          </div>
          <div className="screen-chips">
            {ghosts.map((p) => (
              <PlayerChip key={p.id} player={p} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function GuestListPanel({ pub }: { pub: PublicView }) {
  if (pub.guestList.length === 0) return null;
  return (
    <section className="screen-panel" data-testid="screen-guest-list">
      <h2>The Guest List</h2>
      <ul className="screen-list">
        {pub.guestList.map((g) => (
          <li key={g.role}>
            {roleName(g.role)}
            {g.oneOrMore ? <span className="screen-faint"> (one or more)</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------

function LobbyLayout({ pub, joinUrl, roomCode }: ScreenProps) {
  const config = pub.config;
  return (
    <div className="screen-grid lobby" data-testid="screen-lobby">
      <section className="screen-panel center">
        <div className="screen-label">Scan to join</div>
        <div className="screen-qr">
          <QrCode text={joinUrl} label={`Join QR code for room ${roomCode}`} />
        </div>
        <div className="screen-room-code" data-testid="screen-room-code">
          {roomCode}
        </div>
        <div className="screen-faint">{joinUrl.replace(/^https?:\/\//, "")}</div>
      </section>
      <div className="screen-col">
        <section className="screen-panel">
          <h2>Guests ({pub.roster.length})</h2>
          <div className="screen-chips" data-testid="screen-lobby-roster">
            {pub.roster.map((p) => (
              <PlayerChip key={p.id} player={p} />
            ))}
          </div>
          {pub.roster.length < 6 ? (
            <p className="screen-faint">At least 6 guests are needed.</p>
          ) : null}
        </section>
        <section className="screen-panel">
          <h2>The setup</h2>
          {config ? (
            <p>
              <strong>{presetName(config.presetId) ?? "Random within budget"}</strong>
              <span className="screen-faint">
                {" "}
                · {config.playerCount} players · {config.teams.agents} Agents ·{" "}
                {config.clock.rounds} rounds · Leak limit {config.clock.leakLimit}
              </span>
            </p>
          ) : (
            <p className="screen-faint">The setup follows once enough guests have joined.</p>
          )}
          {pub.rules && pub.rules.changes.length > 0 ? (
            <ul className="screen-list small">
              {pub.rules.changes.slice(0, 6).map((c) => (
                <li key={c.path}>
                  {c.label}: {c.from} → {c.to}
                </li>
              ))}
              {pub.rules.changes.length > 6 ? (
                <li className="screen-faint">and {pub.rules.changes.length - 6} more</li>
              ) : null}
            </ul>
          ) : null}
        </section>
        <section className="screen-panel">
          <h2>Timers</h2>
          <ul className="screen-timers">
            {TIMER_KEYS.map((k) => (
              <li key={k}>
                <span>{TIMER_LABELS[k]}</span>
                <strong>{formatDuration(pub.timers[k])}</strong>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function ArrivalLayout({ pub, now }: ScreenProps) {
  const ms = remainingMs(pub.phase, now);
  return (
    <div className="screen-grid two" data-testid="screen-arrival">
      <div className="screen-col">
        <section className="screen-panel center">
          <div className="screen-hero">Check your role</div>
          <p className="screen-faint">
            Hold your phone close. Mingling starts in {ms !== null ? formatClock(ms) : "a moment"}.
          </p>
        </section>
        <section className="screen-panel">
          <h2>Table rules</h2>
          <ol className="screen-list">
            <li>Never show your screen to another player.</li>
            <li>Say anything you like, including lies.</li>
            <li>Every action happens through the app; a physical handshake alone does nothing.</li>
          </ol>
        </section>
        {pub.rules && pub.rules.changes.length > 0 ? (
          <section className="screen-panel">
            <h2>{pub.rules.badge}</h2>
            <ul className="screen-list small">
              {pub.rules.changes.slice(0, 6).map((c) => (
                <li key={c.path}>
                  {c.label}: {c.from} → {c.to}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      <GuestListPanel pub={pub} />
    </div>
  );
}

function MingleLayout({ pub, now }: ScreenProps) {
  const ms = remainingMs(pub.phase, now);
  return (
    <div className="screen-grid sidebar" data-testid="screen-mingle">
      <div className="screen-col">
        <section className="screen-panel center">
          <div className="screen-label">
            {pub.phase.closing ? "Time's up · handshakes under way are finishing" : "Mingle"}
          </div>
          <div
            className={`screen-countdown ${ms !== null && ms <= 30_000 ? "low" : ""}`}
            data-testid="screen-countdown"
          >
            {ms !== null ? formatClock(ms) : "—"}
          </div>
          <div className="screen-stat" data-testid="screen-handshakes">
            <strong>{pub.handshakesThisRound}</strong>{" "}
            {pub.handshakesThisRound === 1 ? "handshake" : "handshakes"} made in the room this round
          </div>
        </section>
        <section className="screen-panel">
          <BigLeakTrack pub={pub} />
        </section>
        <section className="screen-panel">
          <Roster roster={pub.roster} />
        </section>
      </div>
      <GuestListPanel pub={pub} />
    </div>
  );
}

interface BulletinLine {
  readonly key: string;
  readonly label: string;
  readonly value: ReactNode;
}

function bulletinLines(b: Bulletin, pub: PublicView): BulletinLine[] {
  const names = (ids: readonly string[]) =>
    ids.map((id) => <PlayerChip key={id} player={playerOf(pub, id)} />);
  return [
    { key: "leaks", label: "Leaks this round", value: b.leaks },
    { key: "track", label: "Leak track", value: `${b.leakTrack} / ${b.leakLimit}` },
    { key: "blocked", label: "Blocked attempts", value: b.blocked },
    {
      key: "deaths",
      label: "Found collapsed",
      value: b.deaths.length === 0 ? "No one" : names(b.deaths),
    },
    {
      key: "wallflowers",
      label: "Wallflowers",
      value: b.wallflowersSuppressed
        ? "Not listed (Mingle ended early)"
        : b.wallflowers.length === 0
          ? "None"
          : names(b.wallflowers),
    },
  ];
}

/**
 * When each Bulletin line appears (ms after Dusk starts): after the candles interlude (the same
 * as on the phones), the lines follow one by one, spread over the Dusk timer and never more
 * than 6 s apart, so the whole Bulletin is up well before Dusk ends.
 */
export function bulletinSchedule(duskMs: number, lines: number): number[] {
  const start = candlesMs(duskMs);
  const step = Math.min(6_000, Math.max(500, (duskMs - start) / (lines + 2)));
  return Array.from({ length: lines }, (_, i) => start + (i + 1) * step);
}

function DuskLayout({ pub, now }: ScreenProps) {
  const phase = pub.phase;
  const bulletin = pub.bulletins.find((b) => b.round === phase.round);
  const duration =
    phase.deadline !== null && phase.startedAt !== null ? phase.deadline - phase.startedAt : 60_000;
  // While paused, the reveal holds where it was.
  const at =
    phase.paused && phase.remainingMs !== null
      ? duration - phase.remainingMs
      : now - (phase.startedAt ?? now);
  const elapsed = phase.startedAt === null ? Number.POSITIVE_INFINITY : at;
  if (!bulletin || elapsed < candlesMs(duration)) {
    return (
      <div className="screen-hero-wrap" data-testid="screen-dusk-candles">
        <div className="candles screen-hero">The candles dim…</div>
        <p className="screen-faint">Every handshake of the round is being tallied.</p>
      </div>
    );
  }
  const lines = bulletinLines(bulletin, pub);
  const schedule = bulletinSchedule(duration, lines.length);
  const shown = lines.filter((_, i) => elapsed >= (schedule[i] ?? 0));
  return (
    <div className="screen-grid two" data-testid="screen-dusk">
      <section className="screen-panel" data-testid="screen-bulletin">
        <div className="screen-label">Dusk · Round {bulletin.round}</div>
        <h2>The Bulletin</h2>
        <ul className="screen-bulletin">
          {shown.map((l) => (
            <li key={l.key} className="reveal-line" data-line={l.key}>
              <span>{l.label}</span>
              <strong>{l.value}</strong>
            </li>
          ))}
        </ul>
      </section>
      <div className="screen-col">
        <section className="screen-panel">
          {shown.some((l) => l.key === "track") ? (
            <BigLeakTrack pub={pub} />
          ) : (
            <div className="screen-faint">The Leak track…</div>
          )}
        </section>
        <section className="screen-panel">
          <Roster roster={pub.roster} />
        </section>
      </div>
    </div>
  );
}

function Nominations({ pub }: { pub: PublicView }) {
  const noms = pub.nominations.filter((n) => !n.withdrawn);
  if (noms.length === 0) return <p className="screen-faint">No nominations yet.</p>;
  return (
    <ul className="screen-noms" data-testid="screen-nominations">
      {noms.map((n) => (
        <li key={`${n.window}-${n.nominee}`} className={n.forwardIndex !== null ? "stands" : ""}>
          <PlayerChip player={playerOf(pub, n.nominee)} big />
          <span className="screen-faint">
            nominated by {nameOf(pub, n.nominator)}
            {n.seconder ? ` · seconded by ${nameOf(pub, n.seconder)}` : " · needs a second"}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CouncilLayout({ pub }: ScreenProps) {
  const phase = pub.phase;
  const round = phase.round;
  const reveals = pub.revealedBallots.filter((b) => b.round === round);
  const bulletin = pub.bulletins.find((b) => b.round === round);
  if (phase.kind === "ballot" && pub.ballotProgress) {
    const p = pub.ballotProgress;
    return (
      <div className="screen-grid two" data-testid="screen-ballot">
        <section className="screen-panel center">
          <div className="screen-label">{p.number === 2 ? "Second ballot" : "The ballot"}</div>
          <div className="screen-hero" data-testid="screen-ballot-count">
            {p.cast} / {p.living} ballots in
          </div>
          <p className="screen-faint">Every ballot is revealed at once when the ballot closes.</p>
        </section>
        <section className="screen-panel">
          <h2>Nominees</h2>
          <div className="screen-chips column">
            {p.nominees.map((id) => (
              <PlayerChip key={id} player={playerOf(pub, id)} big />
            ))}
            <span className="screen-faint">or No exile</span>
          </div>
        </section>
      </div>
    );
  }
  if (phase.kind === "defence") {
    const nominee = playerOf(pub, phase.defending);
    return (
      <div className="screen-grid two" data-testid="screen-defence">
        <section className="screen-panel center spotlight">
          <div className="screen-label">In the spotlight</div>
          <div className="screen-spotlight">
            <PlayerChip player={nominee} big />
          </div>
          <p className="screen-faint">makes their defence.</p>
        </section>
        <section className="screen-panel">
          <h2>Nominees</h2>
          <Nominations pub={pub} />
        </section>
      </div>
    );
  }
  return (
    <div className="screen-grid two" data-testid={`screen-council-${phase.kind}`}>
      <div className="screen-col">
        {reveals.map((r) => (
          <RevealPanel key={`${r.round}-${r.number}`} pub={pub} reveal={r} />
        ))}
        <section className="screen-panel">
          <h2>
            {phase.kind === "discussion"
              ? "The Council gathers"
              : phase.kind === "replacement"
                ? "Replacement nominations"
                : "Nominations"}
          </h2>
          {phase.kind === "discussion" ? (
            <p className="screen-faint">
              Claim, accuse, lie. Nominations open when the timer ends.
            </p>
          ) : (
            <Nominations pub={pub} />
          )}
        </section>
      </div>
      <div className="screen-col">
        {bulletin ? (
          <section className="screen-panel">
            <h2>Tonight's Bulletin</h2>
            <ul className="screen-bulletin small">
              {bulletinLines(bulletin, pub).map((l) => (
                <li key={l.key}>
                  <span>{l.label}</span>
                  <strong>{l.value}</strong>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section className="screen-panel">
          <BigLeakTrack pub={pub} />
        </section>
      </div>
    </div>
  );
}

function choiceName(pub: PublicView, choice: string): string {
  return choice === "none" ? "No exile" : nameOf(pub, choice);
}

function RevealPanel({ pub, reveal }: { pub: PublicView; reveal: BallotReveal }) {
  return (
    <section className="screen-panel" data-testid="screen-reveal">
      <div className="screen-label">
        {reveal.number === 2 ? "Second ballot" : "Ballot"} · Round {reveal.round}
      </div>
      <h2 data-testid="screen-reveal-result">
        {reveal.exiled
          ? `${nameOf(pub, reveal.exiled)} is exiled.`
          : BALLOT_OUTCOMES[reveal.outcome]}
      </h2>
      <div className="screen-faint">
        {reveal.tally.map((t) => `${choiceName(pub, t.choice)} ${t.votes}`).join(" · ")} · needed{" "}
        {reveal.threshold}
      </div>
      {reveal.ballots.length > 0 ? (
        <table className="screen-votes">
          <tbody>
            {reveal.ballots.map((b, i) => (
              <tr key={i}>
                <td>{b.voter ? <PlayerChip player={playerOf(pub, b.voter)} /> : "A voter"}</td>
                <td className="screen-faint">→</td>
                <td>
                  {b.choice === "none" ? (
                    "No exile"
                  ) : (
                    <PlayerChip player={playerOf(pub, b.choice)} />
                  )}
                  {b.gavel ? <span className="tag">Gavel ×2</span> : null}
                  {b.ghost ? <span className="tag plain">Ghost</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="screen-faint">Ballots are secret: only the tally is shown.</p>
      )}
    </section>
  );
}

function RevealLayout({ pub, reveals }: { pub: PublicView; reveals: readonly BallotReveal[] }) {
  return (
    <div className="screen-grid reveal" data-testid="screen-ballot-reveal">
      {reveals.map((r) => (
        <RevealPanel key={`${r.round}-${r.number}`} pub={pub} reveal={r} />
      ))}
    </div>
  );
}

/** Scrolls a long panel slowly and loops, so the whole replay passes by on a TV. */
function useAutoScroll<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    let pauseUntil = Date.now() + 5_000;
    const t = setInterval(() => {
      const el = ref.current;
      if (!el || Date.now() < pauseUntil) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
        el.scrollTop = 0;
        pauseUntil = Date.now() + 5_000;
      } else {
        el.scrollTop += 1;
      }
    }, 40);
    return () => clearInterval(t);
  }, []);
  return ref;
}

function EndLayout({ pub, replay, surveys }: ScreenProps) {
  const reveal = pub.reveal;
  const scroller = useAutoScroll<HTMLElement>();
  return (
    <div className="screen-grid two" data-testid="screen-end">
      <div className="screen-col">
        {reveal ? (
          <section className="screen-panel center">
            <div className="screen-label">The gala is over</div>
            <div className="screen-hero" data-testid="screen-winner">
              {reveal.outcome.winner === "syndicate" ? "The Syndicate wins" : "The Delegates win"}
            </div>
            <p className="screen-faint">{outcomeText(reveal.outcome)}</p>
            {surveys && surveys.players > 0 ? (
              <p className="screen-faint" data-testid="screen-surveys">
                Survey on your phone · {surveys.submitted} of {surveys.players} answered
              </p>
            ) : null}
          </section>
        ) : null}
        {reveal ? (
          <section className="screen-panel" data-testid="screen-roles">
            <h2>Everyone's role</h2>
            <ul className="screen-roles">
              {reveal.players.map((p, i) => (
                <li
                  key={p.id}
                  className={`reveal-line ${p.team}`}
                  style={{ animationDelay: `${i * 250}ms` }}
                >
                  <PlayerChip player={playerOf(pub, p.id)} />
                  <span>{roleName(p.role)}</span>
                  <span className={p.team === "syndicate" ? "tag" : "tag plain"}>
                    {p.team === "syndicate" ? "Agent" : "Delegate"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      <section className="screen-panel screen-replay" ref={scroller}>
        {replay ? (
          <Replay replay={replay} allOpen />
        ) : (
          <p className="screen-faint">Loading the replay…</p>
        )}
      </section>
    </div>
  );
}
